import { useCallback, useEffect, useRef } from "react";

import { updateWorkspace } from "../../../services/workspacesApi";
import type { WorkspaceAccountScope } from "../../../types/workspaceAccountScope";
import type { WorkspaceViewport } from "../../../types/workspace";
import { toastWorkspaceApiError } from "../../../utils/workspaceApiErrors";

const DEBOUNCE_MS = 500;

function viewportEqual(a: WorkspaceViewport, b: WorkspaceViewport): boolean {
  return a.x === b.x && a.y === b.y && a.zoom === b.zoom;
}

/** React Flow / API default camera — never treat as a user-saved view. */
export function isDefaultWorkspaceViewport(
  viewport: WorkspaceViewport | null | undefined
): boolean {
  if (!viewport) return true;
  return viewport.x === 0 && viewport.y === 0 && viewport.zoom === 1;
}

export type WorkspaceViewportSaveControls = {
  saveViewport: (viewport: WorkspaceViewport, options?: { immediate?: boolean }) => void;
  /** Remember the restored camera so we do not re-PATCH the same view. */
  seedSavedViewport: (viewport: WorkspaceViewport | null | undefined) => void;
  /** Drop a queued save (e.g. while restore/fit is animating). */
  clearPendingViewportSave: () => void;
};

export function useWorkspaceViewportSave(
  scope: WorkspaceAccountScope | null,
  workspaceId: string | undefined
): WorkspaceViewportSaveControls {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<WorkspaceViewport | null>(null);
  const lastSavedRef = useRef<WorkspaceViewport | null>(null);
  const scopeRef = useRef(scope);
  const workspaceIdRef = useRef(workspaceId);

  scopeRef.current = scope;
  workspaceIdRef.current = workspaceId;

  const clearPendingViewportSave = useCallback(() => {
    pendingRef.current = null;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const seedSavedViewport = useCallback((viewport: WorkspaceViewport | null | undefined) => {
    if (!viewport || isDefaultWorkspaceViewport(viewport)) {
      lastSavedRef.current = null;
      return;
    }
    lastSavedRef.current = viewport;
  }, []);

  const persistViewport = useCallback(async (viewport: WorkspaceViewport) => {
    const activeScope = scopeRef.current;
    const activeWorkspaceId = workspaceIdRef.current;
    if (!activeScope || !activeWorkspaceId) return;
    // Never overwrite a real saved camera with the unset default.
    if (isDefaultWorkspaceViewport(viewport)) return;
    if (lastSavedRef.current && viewportEqual(lastSavedRef.current, viewport)) return;

    try {
      await updateWorkspace(activeScope, activeWorkspaceId, { viewport });
      lastSavedRef.current = viewport;
    } catch (err) {
      toastWorkspaceApiError(err, "Failed to save canvas view");
    }
  }, []);

  useEffect(() => {
    lastSavedRef.current = null;
    pendingRef.current = null;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, [workspaceId]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      const pending = pendingRef.current;
      pendingRef.current = null;
      if (pending && !isDefaultWorkspaceViewport(pending)) {
        void persistViewport(pending);
      }
    };
  }, [persistViewport]);

  const saveViewport = useCallback(
    (viewport: WorkspaceViewport, options?: { immediate?: boolean }) => {
      if (!scopeRef.current || !workspaceIdRef.current) return;
      if (isDefaultWorkspaceViewport(viewport)) return;

      pendingRef.current = viewport;

      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;

      if (options?.immediate) {
        void persistViewport(viewport);
        return;
      }

      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        const next = pendingRef.current;
        if (!next) return;
        void persistViewport(next);
      }, DEBOUNCE_MS);
    },
    [persistViewport]
  );

  return { saveViewport, seedSavedViewport, clearPendingViewportSave };
}
